import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import LocalMallOutlinedIcon from '@mui/icons-material/LocalMallOutlined';
import DesignServicesOutlinedIcon from '@mui/icons-material/DesignServicesOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import AccessibleForwardOutlinedIcon from '@mui/icons-material/AccessibleForwardOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import MyLocationOutlinedIcon from '@mui/icons-material/MyLocationOutlined';
import isVideoFile from '../../utils/isVideoFile';
import getAddressFromCoordinates from '../../utils/getAddressFromCoordinates';
import getCoordinatesFromAddress from '../../utils/getCoordinatesFromAddress';
import getDistanceFromLatLonInMeters from '../../utils/getDistanceFromLatLonInMeters';
import { createMediaUploader } from '../../utils/mediaUpload';
import useMobileView from '../../hooks/useMobileView';
import { formatCoarseLocationLabel } from '../../utils/localOpsLocation';
import {
  COLLISION_SPECIALIST_RIDE_TEMPLATE,
  getCollisionSpecialistRideSuggestedPrice,
} from '../../utils/localOpsCollisionSpecialistRide';
import {
  getLocalOpsConfig,
  formatJGK,
  formatPercent,
} from '../../utils/localOpsConfig';
import LocalOpsSheet from './LocalOpsSheet';
import LocalOpsMediaPicker from './LocalOpsMediaPicker';
import LocalOpsPayWithBalance from './LocalOpsPayWithBalance';

const TYPE_OPTIONS = [
  {
    id: 'offering_product',
    label: 'Offering (Product)',
    mode: 'offering',
    offeringType: 'product',
    group: 'offerings',
    description: 'Eggs, produce, firewood, local goods.',
    tooltip:
      'Great for local goods ready now: eggs, produce, firewood, handmade items.',
    Icon: LocalMallOutlinedIcon,
  },
  {
    id: 'offering_service',
    label: 'Offering (Service)',
    mode: 'offering',
    offeringType: 'service',
    group: 'offerings',
    description: 'Handyman, lawn care, lessons, services.',
    tooltip:
      'Great for services you do well: handyman, lawn care, lessons, services.',
    Icon: DesignServicesOutlinedIcon,
  },
  {
    id: 'task_delivery',
    label: 'Task (Delivery)',
    mode: 'task',
    taskType: 'delivery',
    group: 'tasks',
    description: 'Get an item delivered by a nearby helper.',
    tooltip: 'Ask a nearby helper to deliver an item for you.',
    Icon: LocalShippingOutlinedIcon,
  },
  {
    id: 'task_errand',
    label: 'Task (Errand)',
    mode: 'task',
    taskType: 'errand',
    group: 'tasks',
    description: 'Ask someone local to run a quick task.',
    tooltip: 'Request a quick local errand or favor.',
    Icon: TaskAltOutlinedIcon,
  },
  {
    id: 'task_assistance',
    label: 'Assistance (Transport)',
    mode: 'task',
    taskType: 'assisted_transport',
    group: 'tasks',
    description: 'Request trusted help with a ride (tracked for safety).',
    tooltip: 'Trusted transport help with live tracking for safety.',
    Icon: AccessibleForwardOutlinedIcon,
  },
];

const INFO_COPY =
  'See what locals can do and what locals need right now. Post what you offer or request help nearby.';

const CATEGORY_SUGGESTIONS = [
  'Food',
  'Home',
  'Services',
  'Transport',
  'Errands',
  'Wellness',
  'Education',
  'Tech',
  'Outdoor',
  'Events',
];

const HISTORY_KEY = 'localopsCreateHistory';
const HISTORY_LIMIT = 8;
const DRAFT_PREFIX = 'localopsDraft';
// Presence Phase 2+: prioritize presence-active users by time/routing only.
// Do NOT gate discovery or hide tasks from non-presence users.
const LOCALOPS_PRESENCE_GATING_ENABLED = false;
const LAST_TYPE_KEY = 'localopsLastType';

const coerceFlag = (value, fallback = false) => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'boolean') return value;
  const normalized = String(value).trim().toLowerCase();
  if (['0', 'false', 'off', 'no'].includes(normalized)) return false;
  if (['1', 'true', 'on', 'yes'].includes(normalized)) return true;
  return fallback;
};

const maskCredential = (value) => {
  const raw = String(value || '');
  if (!raw) return '';
  if (raw.length <= 8) return '***';
  return `${raw.slice(0, 4)}...${raw.slice(-4)}`;
};

const maskSensitiveLocalOpsPayload = (value) => {
  if (Array.isArray(value)) return value.map(maskSensitiveLocalOpsPayload);
  if (!value || typeof value !== 'object') return value;
  return Object.entries(value).reduce((next, [key, entry]) => {
    next[key] =
      key === 'intakeDraftToken'
        ? maskCredential(entry)
        : maskSensitiveLocalOpsPayload(entry);
    return next;
  }, {});
};

const isLocalOpsAdminRole = (role) =>
  ['admin', 'galaxyAdmin'].includes(String(role || ''));

const resolveModeFromType = (typeId) =>
  TYPE_OPTIONS.find((opt) => opt.id === typeId)?.mode || null;
const resolveTaskTypeFromType = (typeId) =>
  TYPE_OPTIONS.find((opt) => opt.id === typeId)?.taskType || null;

const makeEmptyHistory = () => ({
  offering: {
    title: [],
    description: [],
    category: [],
    price: [],
    quantity: [],
    media: [],
  },
  task: {
    title: [],
    description: [],
    category: [],
    price: [],
    pickupAddress: [],
    deliveryAddress: [],
    media: [],
  },
});

const normalizeHistory = (value) => {
  const next = makeEmptyHistory();
  if (!value || typeof value !== 'object') return next;
  ['offering', 'task'].forEach((mode) => {
    const bucket = value[mode];
    if (!bucket || typeof bucket !== 'object') return;
    Object.keys(next[mode]).forEach((field) => {
      if (!Array.isArray(bucket[field])) return;
      if (field === 'media') {
        next[mode][field] = bucket[field]
          .map((item) => {
            if (!item) return null;
            if (typeof item === 'string') {
              return { url: item.trim(), type: isVideoFile(item) ? 'video' : 'image' };
            }
            const url = String(item.url || item.preview || '').trim();
            if (!url) return null;
            const type = item.type === 'video' || item.type === 'image' ? item.type : isVideoFile(url) ? 'video' : 'image';
            return { url, type };
          })
          .filter((m) => m && m.url);
      } else {
        next[mode][field] = bucket[field]
          .map((item) => String(item).trim())
          .filter(Boolean);
      }
    });
  });
  return next;
};

const END_MODE_OPTIONS = [
  { id: 'specific', label: 'Specific end time' },
  { id: 'ongoing', label: 'Ongoing' },
  { id: 'supplies', label: 'While supplies last' },
  { id: 'appointment', label: 'By appointment' },
];

const formatLocalDateTime = (date) => {
  if (!date) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const parseLocalDateTime = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const getDefaultSchedule = () => {
  const now = new Date();
  const start = new Date(now.getTime());
  const end = new Date(start.getTime() + 60 * 60 * 1000);
  return {
    start: formatLocalDateTime(start),
    end: formatLocalDateTime(end),
  };
};

const resolveAvailabilityEnd = (startDate, endValue, mode) => {
  if (!startDate) return null;
  if (mode === 'specific') return parseLocalDateTime(endValue);
  const modeDays = {
    ongoing: 30,
    supplies: 7,
    appointment: 14,
  };
  const days = modeDays[mode] || 7;
  return new Date(startDate.getTime() + days * 24 * 60 * 60 * 1000);
};

const labelForEndMode = (mode) => {
  switch (mode) {
    case 'ongoing':
      return 'Ongoing';
    case 'supplies':
      return 'While supplies last';
    case 'appointment':
      return 'By appointment';
    default:
      return 'Specific end';
  }
};

const formatUsdFromJgk = (value, rate) => {
  const safeRate = Number.isFinite(rate) && rate > 0 ? rate : 1000;
  const usd = Number(value) / safeRate;
  if (!Number.isFinite(usd)) return '$0.00';
  return `$${usd.toFixed(2)}`;
};

const formatGroupedJGK = (value) => {
  const amount = Math.round(Number(value));
  if (!Number.isFinite(amount)) return '0 JGK';
  return `${amount.toLocaleString('en-US')} JGK`;
};

export default function LocalOpsCreateSheet({
  open,
  onClose,
  launchId,
  launchTypeHint = null,
  launchStepHint = null,
  defaultCenter,
  radiusMeters,
  onCreateOffering,
  onCreateTask,
  presenceSelf,
  currentPlayerRole = 'player',
  onResolveVerifiedLocalCreatorReadiness = null,
  playerBalance,
  onRequestTopUp,
  onInsufficientBalance = null,
  pendingIntent = null,
  onRetryPendingIntent = () => {},
  friendOptions = [],
  disabledOfferings,
  disabledTasks,
  preset,
}) {
  const uploaderRef = useRef(null);
  const openInitRef = useRef(false);
  const addressKeyRef = useRef(null);
  const pickupGeocodeRef = useRef('');
  const destinationGeocodeRef = useRef('');
  const deliveryPriceGeocodeRef = useRef('');
  const rideAutoPriceRef = useRef('');
  const mobileView = useMobileView();
  const verifiedLocalCreatorToggleEnabled = useMemo(
    () =>
      coerceFlag(
        process.env.REACT_APP_LOCALOPS_VERIFIED_LOCAL_CREATOR_TOGGLE_ENABLED,
        false,
      ),
    [],
  );
  if (!uploaderRef.current) {
    uploaderRef.current = createMediaUploader({});
  }
  const uploader = uploaderRef.current;

  const [step, setStep] = useState(0);
  const [selectedType, setSelectedType] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // AI++ polish: auto-sync description from title until user opts out
  const [descriptionAuto, setDescriptionAuto] = useState(true);
  const [pickupAddress, setPickupAddress] = useState('');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [destinationAddress, setDestinationAddress] = useState('');
  const [category, setCategory] = useState('');
  const [price, setPrice] = useState('');
  const [quantityAvailable, setQuantityAvailable] = useState('1');
  const [requestQuote, setRequestQuote] = useState(false);
  const [deliverySupported, setDeliverySupported] = useState(false);
  const [location, setLocation] = useState(null);
  const [pinSource, setPinSource] = useState('default');
  const [destination, setDestination] = useState(null);
  const [visibility, setVisibility] = useState('nearby');
  const [trustedPlayers, setTrustedPlayers] = useState([]);
  const [requireVerifiedLocal, setRequireVerifiedLocal] = useState(false);
  const [verifiedLocalReadiness, setVerifiedLocalReadiness] = useState({
    status: 'idle',
    canExpose: false,
    key: '',
  });
  const [liveTrackingEnabled, setLiveTrackingEnabled] = useState(true);
  const [availabilityStart, setAvailabilityStart] = useState('');
  const [availabilityEnd, setAvailabilityEnd] = useState('');
  const [availabilityEndMode, setAvailabilityEndMode] = useState('ongoing');
  const [customStart, setCustomStart] = useState(false);
  const [mediaItems, setMediaItems] = useState([]);
  const [coverIndex, setCoverIndex] = useState(0);
  const [error, setError] = useState('');
  const [mediaWarning, setMediaWarning] = useState('');
  const [uploadPct, setUploadPct] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [activeTypeInfo, setActiveTypeInfo] = useState(null);
  const [focusedField, setFocusedField] = useState(null);
  const [inputHistory, setInputHistory] = useState(() => {
    if (typeof window === 'undefined') return makeEmptyHistory();
    try {
      const stored = window.localStorage.getItem(HISTORY_KEY);
      if (!stored) return makeEmptyHistory();
      return normalizeHistory(JSON.parse(stored));
    } catch (err) {
      return makeEmptyHistory();
    }
  });
  const [animateFromFab, setAnimateFromFab] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationLabel, setLocationLabel] = useState('');
  const [isResolvingLocation, setIsResolvingLocation] = useState(false);
  const [pinNotice, setPinNotice] = useState('');
  const [pinError, setPinError] = useState('');
  const [pinHelper, setPinHelper] = useState('');
  const [destinationPinNotice, setDestinationPinNotice] = useState('');
  const [destinationPinError, setDestinationPinError] = useState('');
  const [isPickupGeocoding, setIsPickupGeocoding] = useState(false);
  const [isDestinationGeocoding, setIsDestinationGeocoding] = useState(false);
  const [rideSuggestedPrice, setRideSuggestedPrice] = useState(null);
  const [rideSuggestedDistanceMi, setRideSuggestedDistanceMi] = useState(null);
  const [isRidePriceGeocoding, setIsRidePriceGeocoding] = useState(false);
  const lastTypeRef = useRef(null);
  const draftPlayerId = presenceSelf?.playerId || 'anon';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const stored = window.localStorage.getItem(LAST_TYPE_KEY);
    if (stored) lastTypeRef.current = stored;
  }, []);

  const selectedMeta = useMemo(
    () => TYPE_OPTIONS.find((opt) => opt.id === selectedType) || null,
    [selectedType],
  );
  const isOffering = selectedMeta?.mode === 'offering';
  const isTask = selectedMeta?.mode === 'task';
  const isDelivery = selectedMeta?.taskType === 'delivery';
  const isAssistance = selectedMeta?.taskType === 'assisted_transport';
  const isCollisionSpecialistRidePreset =
    preset?.rideTemplate === COLLISION_SPECIALIST_RIDE_TEMPLATE;
  const draftKey =
    selectedMeta && draftPlayerId
      ? `${DRAFT_PREFIX}:${draftPlayerId}:${selectedMeta.mode || 'unknown'}`
      : null;
  useEffect(() => {
    if (!isOffering) return;
    if (requestQuote) setRequestQuote(false);
    if (quantityAvailable === '') setQuantityAvailable('1');
  }, [isOffering, requestQuote, quantityAvailable]);
  const historyMode = isOffering ? 'offering' : isTask ? 'task' : null;
  const titleHistory = useMemo(
    () => (historyMode ? inputHistory[historyMode]?.title || [] : []),
    [historyMode, inputHistory],
  );
  const descriptionHistory = useMemo(
    () =>
      historyMode ? inputHistory[historyMode]?.description || [] : [],
    [historyMode, inputHistory],
  );
  const descriptionSuggestions = useMemo(
    () => descriptionHistory.slice(0, 3),
    [descriptionHistory],
  );
  const categoryHistory = useMemo(
    () => (historyMode ? inputHistory[historyMode]?.category || [] : []),
    [historyMode, inputHistory],
  );
  const priceHistory = useMemo(
    () => (historyMode ? inputHistory[historyMode]?.price || [] : []),
    [historyMode, inputHistory],
  );
  const pickupHistory = useMemo(
    () =>
      historyMode === 'task'
        ? inputHistory.task?.pickupAddress || []
        : historyMode === 'offering'
        ? inputHistory.offering?.pickupAddress || []
        : [],
    [historyMode, inputHistory],
  );
  const deliveryHistory = useMemo(
    () =>
      historyMode === 'task'
        ? inputHistory.task?.deliveryAddress || []
        : [],
    [historyMode, inputHistory],
  );
  const destinationHistory = useMemo(
    () =>
      historyMode === 'task'
        ? inputHistory.task?.deliveryAddress || []
        : [],
    [historyMode, inputHistory],
  );
  const recentTitleChips = useMemo(
    () => titleHistory.slice(0, 3),
    [titleHistory],
  );

  // AI++: auto-sync description from title until user edits description
  useEffect(() => {
    if (!descriptionAuto) return;
    if (!title) {
      if (description) setDescription('');
      return;
    }
    setDescription(title);
  }, [title, description, descriptionAuto]);
  const categoryOptions = useMemo(() => {
    const seen = new Set();
    const combined = [...categoryHistory, ...CATEGORY_SUGGESTIONS];
    return combined.filter((item) => {
      const normalized = String(item).trim().toLowerCase();
      if (!normalized || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
  }, [categoryHistory]);
  const pickupAddressValue = useMemo(
    () => pickupAddress.trim(),
    [pickupAddress],
  );
  const deliveryAddressValue = useMemo(
    () => deliveryAddress.trim(),
    [deliveryAddress],
  );
  const destinationAddressValue = useMemo(
    () => destinationAddress.trim(),
    [destinationAddress],
  );
  const pickupCoordsValid =
    location && Number.isFinite(location.lat) && Number.isFinite(location.lon);
  const verifiedLocalCreatorIsAdmin = isLocalOpsAdminRole(currentPlayerRole);
  const verifiedLocalReadinessKey =
    pickupCoordsValid && isTask
      ? `${Number(location.lat).toFixed(5)}|${Number(location.lon).toFixed(
          5,
        )}|${Math.round(Number(radiusMeters || 0))}`
      : '';
  const canShowRequireVerifiedLocal =
    verifiedLocalCreatorToggleEnabled &&
    isTask &&
    (verifiedLocalCreatorIsAdmin ||
      (verifiedLocalReadiness.status === 'ready' &&
        verifiedLocalReadiness.key === verifiedLocalReadinessKey &&
        verifiedLocalReadiness.canExpose === true));
  const destinationCoordsValid =
    destination &&
    Number.isFinite(destination.lat) &&
    Number.isFinite(destination.lon);
  const showDestinationPinError =
    destinationAddressValue && !destinationCoordsValid;
  const isPublishBlockedByDestination =
    isAssistance && destinationAddressValue && !destinationCoordsValid;
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[DEBUG][STEP]', step, {
      destination,
      destinationCoordsValid,
      destinationAddressValue,
      pickupAddressValue,
      pickupCoordsValid,
      isPickupGeocoding,
      isDestinationGeocoding,
      location,
    });
    console.assert(
      !destinationCoordsValid || !showDestinationPinError,
      'INVARIANT VIOLATION: destination has coordinates but destination pin error is shown.',
    );
    console.assert(
      !destinationCoordsValid || !isPublishBlockedByDestination,
      'INVARIANT VIOLATION: destination has coordinates but publish is still blocked.',
    );
  }
  useEffect(() => {
    if (
      !open ||
      !verifiedLocalCreatorToggleEnabled ||
      !isTask ||
      verifiedLocalCreatorIsAdmin
    ) {
      setVerifiedLocalReadiness({ status: 'idle', canExpose: false, key: '' });
      return;
    }
    if (!pickupCoordsValid || !verifiedLocalReadinessKey) {
      setVerifiedLocalReadiness({
        status: 'missing_location',
        canExpose: false,
        key: '',
      });
      return;
    }
    if (typeof onResolveVerifiedLocalCreatorReadiness !== 'function') {
      setVerifiedLocalReadiness({
        status: 'unavailable',
        canExpose: false,
        key: verifiedLocalReadinessKey,
      });
      return;
    }

    let active = true;
    setVerifiedLocalReadiness({
      status: 'loading',
      canExpose: false,
      key: verifiedLocalReadinessKey,
    });
    Promise.resolve(
      onResolveVerifiedLocalCreatorReadiness({
        lat: location.lat,
        lng: location.lon,
        radiusMeters,
      }),
    )
      .then((result) => {
        if (!active) return;
        setVerifiedLocalReadiness({
          status: 'ready',
          canExpose:
            result?.canExposeVerifiedLocalCreatorToggle === true ||
            result?.canExpose === true,
          key: verifiedLocalReadinessKey,
        });
      })
      .catch(() => {
        if (!active) return;
        setVerifiedLocalReadiness({
          status: 'error',
          canExpose: false,
          key: verifiedLocalReadinessKey,
        });
      });
    return () => {
      active = false;
    };
  }, [
    open,
    verifiedLocalCreatorToggleEnabled,
    isTask,
    verifiedLocalCreatorIsAdmin,
    pickupCoordsValid,
    verifiedLocalReadinessKey,
    onResolveVerifiedLocalCreatorReadiness,
    location,
    radiusMeters,
  ]);
  const pickupPinMatches =
    pickupAddressValue &&
    pickupGeocodeRef.current === pickupAddressValue;
  const showPickupPinAction =
    isTask &&
    pickupAddressValue &&
    !(pinSource === 'pickup' && pickupPinMatches);
  const showInfoCard = showInfo;
  const headerTitle = useMemo(() => {
    if (!selectedMeta) return 'Create LocalOps';
    if (selectedMeta.mode === 'offering') {
      return selectedMeta.offeringType === 'service'
        ? 'Create Service Offering'
        : 'Create Product Offering';
    }
    if (selectedMeta.taskType === 'delivery') {
      return isCollisionSpecialistRidePreset
        ? 'Request Ride Help'
        : 'Create Delivery Task';
    }
    if (selectedMeta.taskType === 'errand') return 'Create Errand Task';
    if (selectedMeta.taskType === 'assisted_transport')
      return 'Create Assistance Task';
    return 'Create Task';
  }, [selectedMeta, isCollisionSpecialistRidePreset]);
  const sheetPositionClass = useMemo(() => {
    if (mobileView) return '';
    return step > 0 ? 'localops-sheet--top' : 'localops-sheet--centered';
  }, [mobileView, step]);
  const sheetClassName = useMemo(
    () =>
      [animateFromFab ? 'localops-sheet--from-fab' : '', sheetPositionClass]
        .filter(Boolean)
        .join(' '),
    [animateFromFab, sheetPositionClass],
  );
  const localOpsConfig = useMemo(() => getLocalOpsConfig(), []);
  const escrowRequiredJGK = useMemo(() => {
    if (!isTask || requestQuote) return 0;
    const numericPrice = Number(price);
    if (!Number.isFinite(numericPrice) || numericPrice <= 0) return 0;
    const rate = Number.isFinite(localOpsConfig?.jgkPerUsd)
      ? localOpsConfig.jgkPerUsd
      : 1000;
    return Math.round(numericPrice * rate);
  }, [isTask, requestQuote, price, localOpsConfig]);
  const escrowLabel = isCollisionSpecialistRidePreset
    ? formatGroupedJGK(escrowRequiredJGK)
    : formatJGK(escrowRequiredJGK);
  const escrowUsdLabel = useMemo(
    () => formatUsdFromJgk(escrowRequiredJGK, localOpsConfig?.jgkPerUsd),
    [escrowRequiredJGK, localOpsConfig],
  );
  const feeLabel = formatPercent(localOpsConfig.feePercent);
  const hasPaidTaskAmount = isTask && escrowRequiredJGK > 0;
  const rideReviewAmountLabel = useMemo(() => {
    if (requestQuote) return 'Not set yet';
    const numericPrice = Number(price);
    if (!Number.isFinite(numericPrice) || numericPrice <= 0) return 'Not set yet';
    return numericPrice % 1 === 0
      ? `$${numericPrice.toFixed(0)}`
      : `$${numericPrice.toFixed(2)}`;
  }, [price, requestQuote]);
  const rideSuggestedDistanceLabel = useMemo(() => {
    const miles = Number(rideSuggestedDistanceMi);
    if (!Number.isFinite(miles)) return null;
    if (miles < 0.1) return 'under 0.1 mi';
    return `${miles.toFixed(1)} mi`;
  }, [rideSuggestedDistanceMi]);
  const publishLabel = useMemo(() => {
    if (isSubmitting) return 'Publishing...';
    if (escrowRequiredJGK > 0) {
      return isCollisionSpecialistRidePreset
        ? 'Approve & request ride help'
        : 'Approve & Publish';
    }
    return isCollisionSpecialistRidePreset ? 'Request ride help' : 'Publish';
  }, [isSubmitting, escrowRequiredJGK, isCollisionSpecialistRidePreset]);
  const showDescriptionHistory =
    descriptionSuggestions.length > 0 &&
    (focusedField === 'description' || !description.trim());
  const handleUsePickupPin = useCallback(async () => {
    if (!pickupAddressValue || isPickupGeocoding) return;
    setIsPickupGeocoding(true);
    setPinNotice('');
    setPinError('');
    try {
      const coords = await getCoordinatesFromAddress(pickupAddressValue);
      if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lon)) {
        throw new Error('No results');
      }
      setLocation({ lat: coords.lat, lon: coords.lon });
      setPinSource('pickup');
      pickupGeocodeRef.current = pickupAddressValue;
      setPinNotice('Pin placed near pickup location.');
      setPinHelper('');
    } catch (err) {
      setPinError("Couldn't find that address. You can move the pin manually.");
    } finally {
      setIsPickupGeocoding(false);
    }
  }, [pickupAddressValue, isPickupGeocoding]);

  // Auto-geocode destination address for assistance tasks
  useEffect(() => {
    if (!isAssistance) return;
    if (!destinationAddressValue) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[DEBUG][DEST GEO] clear because address empty', {
          step,
          destinationAddressValue,
        });
      }
      setDestination(null);
      setDestinationPinNotice('');
      setDestinationPinError('');
      destinationGeocodeRef.current = '';
      return;
    }
    const current = destinationAddressValue;
    const alreadyGeocoded = destinationGeocodeRef.current === current;
    if (alreadyGeocoded && destinationCoordsValid) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.log('[DEBUG][DEST GEO] skip, already geocoded & coords still valid', {
          step,
          destinationAddressValue: current,
          destination,
        });
      }
      return;
    }
    setDestinationPinNotice('');
    setDestinationPinError('');
    const timer = setTimeout(async () => {
      setIsDestinationGeocoding(true);
      try {
        const coords = await getCoordinatesFromAddress(current);
        // If address changed during lookup, ignore this result
        if (current !== destinationAddressValue) return;
        if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lon)) {
          throw new Error('No results');
        }
        setDestination({ lat: coords.lat, lon: coords.lon });
        destinationGeocodeRef.current = current;
        setDestinationPinNotice('Pin placed near destination.');
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[DEBUG][DEST GEO] success', {
            step,
            address: current,
            coords,
          });
        }
      } catch (err) {
        if (current !== destinationAddressValue) return;
        setDestinationPinError("Couldn't find that address. You can move the pin manually.");
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.log('[DEBUG][DEST GEO] fail', {
            step,
            address: current,
            error: err?.message,
          });
        }
      } finally {
        if (current === destinationAddressValue) {
          setIsDestinationGeocoding(false);
        }
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [
    destinationAddressValue,
    isAssistance,
    setDestinationPinError,
    setDestinationPinNotice,
  ]);
  const handleUseDestinationPin = useCallback(async () => {
    if (!destinationAddressValue || isDestinationGeocoding) return;
    setIsDestinationGeocoding(true);
    setDestinationPinNotice('');
    setDestinationPinError('');
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[DEBUG][DEST PIN BTN] manual geocode start', {
        destinationAddressValue,
        step,
      });
    }
    try {
      const coords = await getCoordinatesFromAddress(destinationAddressValue);
      if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lon)) {
        throw new Error('No results');
      }
      setDestination({ lat: coords.lat, lon: coords.lon });
      destinationGeocodeRef.current = destinationAddressValue;
      setDestinationPinNotice('Pin placed near destination.');
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[DEBUG][DEST PIN BTN] success', {
          coords,
        });
      }
    } catch (err) {
      setDestinationPinError("Couldn't find that address. You can move the pin manually.");
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[DEBUG][DEST PIN BTN] fail', {
          error: err?.message,
        });
      }
    } finally {
      setIsDestinationGeocoding(false);
    }
  }, [destinationAddressValue, isDestinationGeocoding]);

  // Auto-geocode pickup to pin when using default pinSource
  useEffect(() => {
    if (!isTask && !isOffering) return;
    if (!pickupAddressValue) {
      setPinHelper('');
      return;
    }
    if (pinSource !== 'default') return;
    if (pickupGeocodeRef.current === pickupAddressValue) return;
    const current = pickupAddressValue;
    const timer = setTimeout(async () => {
      setIsPickupGeocoding(true);
      try {
        const coords = await getCoordinatesFromAddress(current);
        if (pickupAddressValue !== current) return;
        if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lon)) {
          throw new Error('No results');
        }
        setLocation({ lat: coords.lat, lon: coords.lon });
        setPinSource('pickup');
        pickupGeocodeRef.current = current;
        setPinHelper('Pin automatically placed near pickup.');
        setPinNotice('Pin placed near pickup location.');
      } catch (err) {
        if (pickupAddressValue !== current) return;
        setPinHelper('');
      } finally {
        if (pickupAddressValue === current) {
          setIsPickupGeocoding(false);
        }
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [pickupAddressValue, isTask, pinSource]);
  const isFree = !requestQuote && price !== '' && Number(price) === 0;
  const previewData = useMemo(() => {
    const baseTitle =
      title.trim() ||
      (isOffering ? 'New offering' : 'New task');
    const typeLabel =
      selectedMeta?.label ||
      (isOffering ? 'Offering' : 'Task');
    // Keep preview honest: offerings require category, so avoid masking missing input.
    const categoryLabel = isCollisionSpecialistRidePreset
      ? 'Ride help'
      : category.trim() || (isOffering ? 'Category required' : typeLabel);
    let priceLabel = '';
    if (!isOffering && requestQuote) {
      priceLabel = 'Request quote';
    } else if (price === '') {
      priceLabel = isOffering ? 'Price required' : 'Price not set';
    } else if (Number(price) === 0) {
      priceLabel = 'Free';
    } else if (!Number.isNaN(Number(price))) {
      priceLabel = `$${price}`;
    } else {
      priceLabel = isOffering ? 'Price required' : 'Price not set';
    }
    let quantityLabel = '';
    if (isOffering) {
      const qtyValue = Number(quantityAvailable);
      quantityLabel =
        Number.isInteger(qtyValue) && qtyValue > 0
          ? `${qtyValue} available`
          : 'Quantity required';
    }
    return {
      title: baseTitle,
      description: description.trim(),
      category: categoryLabel,
      priceLabel,
      quantityLabel,
      location: locationLabel || 'Pinned to map',
      endMode: availabilityEndMode === 'specific'
        ? null
        : labelForEndMode(availabilityEndMode),
      media: mediaItems.slice(0, 3),
    };
  }, [
    title,
    description,
    isOffering,
    selectedMeta,
    isCollisionSpecialistRidePreset,
    category,
    requestQuote,
    price,
    quantityAvailable,
    locationLabel,
    availabilityEndMode,
    mediaItems,
  ]);

  const presenceReady = useMemo(() => {
    if (!LOCALOPS_PRESENCE_GATING_ENABLED) return true;
    if (!presenceSelf) return true;
    const presenceActive =
      typeof presenceSelf.presenceActive === 'boolean'
        ? presenceSelf.presenceActive
        : true;
    const presenceStatus =
      presenceSelf.presenceStatus || presenceSelf.status || 'online';
    return presenceActive && presenceStatus !== 'offline';
  }, [presenceSelf]);

  const clearMediaItems = useCallback(() => {
    setMediaItems((prev) => {
      prev.forEach((item) => {
        if (item.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(item.preview);
        }
      });
      return [];
    });
    setCoverIndex(0);
  }, []);

  const resetForm = useCallback(
    ({ nextType = null, nextStep = 0, keepLocation = false } = {}) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[DEBUG][RESET FORM]', {
          nextType,
          nextStep,
          keepLocation,
          selectedType,
          step,
        });
      }
      const fallbackType =
        nextType || selectedType || lastTypeRef.current || null;
      const mode = resolveModeFromType(fallbackType);
      const pickLast = (list) =>
        Array.isArray(list) && list.length ? String(list[0]) : '';
      const historyBucket = mode ? inputHistory[mode] || {} : {};
      setTitle(pickLast(historyBucket.title) || '');
      setDescription(pickLast(historyBucket.description) || '');
      if (!keepLocation) {
        setPickupAddress(
          mode === 'task' ? pickLast(historyBucket.pickupAddress) || '' : '',
        );
        setDeliveryAddress(
          mode === 'task' ? pickLast(historyBucket.deliveryAddress) || '' : '',
        );
        setDestinationAddress(
          mode === 'task' ? pickLast(historyBucket.deliveryAddress) || '' : '',
        );
      }
      setCategory(pickLast(historyBucket.category) || '');
      setPrice(pickLast(historyBucket.price) || '');
      if (mode === 'offering') {
        setQuantityAvailable(pickLast(historyBucket.quantity) || '1');
      } else {
        setQuantityAvailable('1');
      }
      setRequestQuote(false);
      setDeliverySupported(false);
      if (!keepLocation) {
        setDestination(null);
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[DEBUG][DEST RESET]', {
            reason: 'resetForm',
            step,
            destinationAddressValue,
          });
        }
      }
      setVisibility('nearby');
      setTrustedPlayers([]);
      setRequireVerifiedLocal(false);
      setLiveTrackingEnabled(true);
      if (!keepLocation) {
        setPinSource('default');
        setPinNotice('');
        setPinError('');
        setDestinationPinNotice('');
        setDestinationPinError('');
        setIsPickupGeocoding(false);
        setIsDestinationGeocoding(false);
        pickupGeocodeRef.current = '';
        destinationGeocodeRef.current = '';
        deliveryPriceGeocodeRef.current = '';
        rideAutoPriceRef.current = '';
      }
      setRideSuggestedPrice(null);
      setRideSuggestedDistanceMi(null);
      setIsRidePriceGeocoding(false);
      setError('');
      setUploadPct(0);
      setIsSubmitting(false);
      setShowInfo(false);
      setShowAdvanced(false);
      setActiveTypeInfo(null);
      setFocusedField(null);
      setSearchTerm('');
      setAvailabilityEndMode('ongoing');
      const nextSchedule = getDefaultSchedule();
      setAvailabilityStart(nextSchedule.start);
      setAvailabilityEnd(nextSchedule.end);
      setCustomStart(false);
      if (!keepLocation) {
        addressKeyRef.current = null;
        setLocationLabel('');
        setIsResolvingLocation(false);
        setLocation(defaultCenter ? { ...defaultCenter } : null);
      }
      clearMediaItems();
      setSelectedType(fallbackType);
      if (fallbackType && typeof window !== 'undefined') {
        lastTypeRef.current = fallbackType;
        try {
          window.localStorage.setItem(LAST_TYPE_KEY, fallbackType);
        } catch (err) {
          // ignore storage failures
        }
      }
      setStep(nextStep);
    },
    [clearMediaItems, defaultCenter, inputHistory, selectedType],
  );

  const handleSelectType = useCallback(
    (typeId) => {
      resetForm({ nextType: typeId, nextStep: 1, keepLocation: true });
      lastTypeRef.current = typeId;
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(LAST_TYPE_KEY, typeId);
        } catch (err) {
          // ignore storage failures
        }
      }
    },
    [resetForm],
  );

  useEffect(() => {
    if (!open || !launchId) return;
    setAnimateFromFab(true);
    const timer = setTimeout(() => setAnimateFromFab(false), 420);
    return () => clearTimeout(timer);
  }, [open, launchId]);

  useEffect(() => {
    if (!open) {
      openInitRef.current = false;
      return;
    }
    if (openInitRef.current) return;
    openInitRef.current = true;
    const nextType = preset?.type || launchTypeHint || null;
    const nextStep = preset?.type
      ? 1
      : Number.isInteger(launchStepHint)
      ? launchStepHint
      : nextType
      ? 1
      : 0;
    resetForm({ nextType, nextStep });
  }, [open, preset, launchTypeHint, launchStepHint, resetForm]);

  // Draft persistence (debounced)
  useEffect(() => {
    if (!draftKey) return undefined;
    const timer = setTimeout(() => {
      try {
        const payload = {
          title,
          description,
          pickupAddress,
          deliveryAddress,
          destinationAddress,
          category,
          price,
          quantityAvailable,
          location,
          pinSource,
          availabilityStart,
          availabilityEnd,
          availabilityEndMode,
          visibility,
          trustedPlayers,
        };
        window.localStorage.setItem(draftKey, JSON.stringify(payload));
      } catch (err) {
        // ignore draft failures
      }
    }, 700);
    return () => clearTimeout(timer);
  }, [
    draftKey,
    title,
    description,
    pickupAddress,
    deliveryAddress,
    destinationAddress,
    category,
    price,
    quantityAvailable,
    location,
    pinSource,
    availabilityStart,
    availabilityEnd,
    availabilityEndMode,
    visibility,
    trustedPlayers,
  ]);

  useEffect(() => {
    if (!open || !preset?.type) return;
    setSelectedType(preset.type);
    setStep(1);
    if (preset.title) setTitle(preset.title);
    if (preset.description) {
      setDescription(preset.description);
      setDescriptionAuto(false);
    }
    if (preset.category) setCategory(preset.category);
    if (preset.pickupAddress) setPickupAddress(preset.pickupAddress);
    if (preset.deliveryAddress) setDeliveryAddress(preset.deliveryAddress);
    if (preset.price !== undefined && preset.price !== null) {
      setPrice(String(preset.price));
      setRequestQuote(false);
    }
    if (preset.rideTemplate === COLLISION_SPECIALIST_RIDE_TEMPLATE) {
      setRequestQuote(false);
    }
    if (preset.origin) {
      setLocation({ ...preset.origin });
      if (preset.pickupAddress) {
        pickupGeocodeRef.current = preset.pickupAddress;
        setLocationLabel(preset.pickupAddress);
      }
      const lat = Number(preset.origin.lat);
      const lon = Number(preset.origin.lon);
      if (Number.isFinite(lat) && Number.isFinite(lon)) {
        addressKeyRef.current = `${lat.toFixed(4)}|${lon.toFixed(4)}`;
      }
    }
    if (preset.destination && preset.type === 'task_assistance') {
      setDestination({ ...preset.destination });
      if (preset.destinationAddress) {
        setDestinationAddress(preset.destinationAddress);
      }
    }
  }, [open, preset]);

  // Draft restore (per player + mode)
  useEffect(() => {
    if (!open || !draftKey || preset) return;
    if (step !== 0 && step !== 1) return;
    try {
      const stored = window.localStorage.getItem(draftKey);
      if (!stored) return;
      const draft = JSON.parse(stored);
      if (!draft || typeof draft !== 'object') return;
      setError('');
      if (draft.title) setTitle(draft.title);
      if (draft.description) {
        setDescription(draft.description);
        setDescriptionAuto(draft.description === draft.title);
      }
      if (draft.pickupAddress) setPickupAddress(draft.pickupAddress);
      if (draft.deliveryAddress) setDeliveryAddress(draft.deliveryAddress);
      if (draft.destinationAddress) setDestinationAddress(draft.destinationAddress);
      if (draft.category) setCategory(draft.category);
      if (draft.price !== undefined) setPrice(String(draft.price));
      if (draft.quantityAvailable) setQuantityAvailable(String(draft.quantityAvailable));
      if (draft.location && Number.isFinite(draft.location.lat) && Number.isFinite(draft.location.lon)) {
        setLocation({ ...draft.location });
      }
      if (draft.pinSource) setPinSource(draft.pinSource);
      if (draft.availabilityStart) setAvailabilityStart(draft.availabilityStart);
      if (draft.availabilityEnd) setAvailabilityEnd(draft.availabilityEnd);
      if (draft.availabilityEndMode) setAvailabilityEndMode(draft.availabilityEndMode);
      if (draft.visibility) setVisibility(draft.visibility);
      if (draft.trustedPlayers) setTrustedPlayers(draft.trustedPlayers);
    } catch (err) {
      // ignore draft errors
    }
  }, [open, draftKey, preset, step]);

  useEffect(() => {
    if (!open || !defaultCenter) return;
    setLocation((prev) => prev || { ...defaultCenter });
  }, [open, defaultCenter]);

  useEffect(() => {
    if (!open || !location) return;
    if (!Number.isFinite(location.lat) || !Number.isFinite(location.lon)) {
      setLocationLabel('');
      return;
    }
    const key = `${location.lat.toFixed(4)}|${location.lon.toFixed(4)}`;
    if (addressKeyRef.current === key) return;
    addressKeyRef.current = key;
    let active = true;
    setIsResolvingLocation(true);
    getAddressFromCoordinates(location.lat, location.lon)
      .then((address) => {
        if (!active) return;
        const label =
          formatCoarseLocationLabel(address) ||
          `${location.lat.toFixed(3)}, ${location.lon.toFixed(3)}`;
        setLocationLabel(label);
      })
      .catch(() => {
        if (!active) return;
        setLocationLabel(
          `${location.lat.toFixed(3)}, ${location.lon.toFixed(3)}`,
        );
      })
      .finally(() => {
        if (!active) return;
        setIsResolvingLocation(false);
      });
    return () => {
      active = false;
    };
  }, [open, location]);

  // Geocode any addresses whenever present; rerun when address changes even if coords exist
  useEffect(() => {
    if (!open) return;
    const coordsValid = (loc) =>
      loc && Number.isFinite(loc.lat) && Number.isFinite(loc.lon);
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.debug('[LocalOpsCreateSheet][prefill-geo:start]', {
        step,
        pickupAddressValue,
        destinationAddressValue,
        pickupCoordsValid: coordsValid(location),
        destinationCoordsValid: coordsValid(destination),
      });
    }
    (async () => {
      if (
        pickupAddressValue &&
        pickupAddressValue !== pickupGeocodeRef.current &&
        !isPickupGeocoding
      ) {
        try {
          setIsPickupGeocoding(true);
          const coords = await getCoordinatesFromAddress(pickupAddressValue);
          setLocation({ lat: coords.lat, lon: coords.lon });
          pickupGeocodeRef.current = pickupAddressValue;
          setPinNotice('Location confirmed.');
          setPinError('');
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[LocalOpsCreateSheet][prefill-geo:pickup:success]', {
              address: pickupAddressValue,
              coords,
              step,
            });
          }
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[LocalOpsCreateSheet][prefill-geo:pickup:fail]', {
              address: pickupAddressValue,
              error: err?.message,
              step,
            });
          }
        } finally {
          setIsPickupGeocoding(false);
        }
      }
      if (
        destinationAddressValue &&
        !coordsValid(destination) &&
        !isDestinationGeocoding
      ) {
        try {
          setIsDestinationGeocoding(true);
          const coords = await getCoordinatesFromAddress(destinationAddressValue);
          setDestination({ lat: coords.lat, lon: coords.lon });
          destinationGeocodeRef.current = destinationAddressValue;
          setDestinationPinNotice('Destination confirmed.');
          setDestinationPinError('');
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[LocalOpsCreateSheet][prefill-geo:dest:success]', {
              address: destinationAddressValue,
              coords,
              step,
            });
          }
        } catch (err) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.debug('[LocalOpsCreateSheet][prefill-geo:dest:fail]', {
              address: destinationAddressValue,
              error: err?.message,
              step,
            });
          }
        } finally {
          setIsDestinationGeocoding(false);
        }
      }
    })();
  }, [open, pickupAddressValue, destinationAddressValue]);

  useEffect(() => {
    if (!open || !isCollisionSpecialistRidePreset || !isDelivery) {
      setRideSuggestedPrice(null);
      setRideSuggestedDistanceMi(null);
      deliveryPriceGeocodeRef.current = '';
      return;
    }
    if (!deliveryAddressValue) {
      setRideSuggestedPrice(null);
      setRideSuggestedDistanceMi(null);
      deliveryPriceGeocodeRef.current = '';
      return;
    }
    if (!pickupCoordsValid) return;

    const geocodeKey = `${pickupAddressValue}|${deliveryAddressValue}`;
    if (deliveryPriceGeocodeRef.current === geocodeKey) return;

    let active = true;
    setIsRidePriceGeocoding(true);
    getCoordinatesFromAddress(deliveryAddressValue)
      .then((coords) => {
        if (!active) return;
        if (!coords || !Number.isFinite(coords.lat) || !Number.isFinite(coords.lon)) {
          throw new Error('Delivery address did not resolve.');
        }
        const meters = getDistanceFromLatLonInMeters(
          location.lat,
          location.lon,
          coords.lat,
          coords.lon,
        );
        const miles = meters / 1609.344;
        const suggested = getCollisionSpecialistRideSuggestedPrice(miles);
        if (!Number.isFinite(suggested)) return;
        const suggestedString = String(suggested);
        setRideSuggestedPrice(suggested);
        setRideSuggestedDistanceMi(miles);
        deliveryPriceGeocodeRef.current = geocodeKey;
        setPrice((prev) => {
          const current = String(prev || '').trim();
          if (!current || current === rideAutoPriceRef.current) {
            rideAutoPriceRef.current = suggestedString;
            return suggestedString;
          }
          return prev;
        });
      })
      .catch(() => {
        if (!active) return;
        setRideSuggestedPrice(null);
        setRideSuggestedDistanceMi(null);
      })
      .finally(() => {
        if (!active) return;
        setIsRidePriceGeocoding(false);
      });

    return () => {
      active = false;
    };
  }, [
    open,
    isCollisionSpecialistRidePreset,
    isDelivery,
    deliveryAddressValue,
    pickupAddressValue,
    pickupCoordsValid,
    location,
  ]);

  // When pickup address changes, clear stale coords so geocode reruns
  useEffect(() => {
    if (!open) return;
    if (!pickupAddressValue) return;
    if (pickupAddressValue !== pickupGeocodeRef.current) {
      setLocation(null);
    }
  }, [open, pickupAddressValue]);

  useEffect(() => {
    if (!pickupAddressValue) {
      if (pinNotice) setPinNotice('');
      if (pinError) setPinError('');
      return;
    }
    if (
      pickupGeocodeRef.current &&
      pickupAddressValue !== pickupGeocodeRef.current
    ) {
      if (pinNotice) setPinNotice('');
      if (pinError) setPinError('');
    }
  }, [pickupAddressValue, pinNotice, pinError]);

  useEffect(() => {
    if (isAssistance && visibility !== 'trusted') {
      setVisibility('trusted');
    }
  }, [isAssistance, visibility]);

  useEffect(() => {
    if (!canShowRequireVerifiedLocal) {
      setRequireVerifiedLocal(false);
    }
  }, [canShowRequireVerifiedLocal]);

  useEffect(() => {
    return () => {
      mediaItems.forEach((item) => {
        if (item.preview?.startsWith('blob:')) {
          URL.revokeObjectURL(item.preview);
        }
      });
    };
  }, [mediaItems]);

  const mediaNeedsReattach = useMemo(
    () => mediaItems.some((item) => item.needsReattach || item.placeholder),
    [mediaItems],
  );

  useEffect(() => {
    if (!mediaNeedsReattach && mediaWarning) {
      setMediaWarning('');
    }
  }, [mediaNeedsReattach, mediaWarning]);

  const toggleTrusted = useCallback((id) => {
    setTrustedPlayers((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  }, []);

  const addFiles = useCallback(
    (files) => {
      const list = Array.from(files || []);
      if (!list.length) return;
      const next = [];
      const errs = [];
      list.forEach((file) => {
        try {
          uploader.validate(file);
          const preview = URL.createObjectURL(file);
          next.push({
            file,
            preview,
            isVideo: isVideoFile(file.name),
            needsReattach: false,
          });
        } catch (err) {
          errs.push(err.message || `${file.name} is not valid.`);
        }
      });
      if (errs.length) setError(errs.join(' | '));
      setMediaItems((prev) => [...prev, ...next]);
      setMediaWarning('');
      setError((prev) =>
        prev && prev.toLowerCase().includes('reattach') ? '' : prev,
      );
    },
    [uploader],
  );

  const removeMedia = useCallback((idx) => {
    setMediaItems((prev) => {
      const next = [...prev];
      const removed = next.splice(idx, 1);
      removed.forEach((item) => {
        if (item.preview?.startsWith('blob:')) URL.revokeObjectURL(item.preview);
      });
      return next;
    });
    setCoverIndex((prev) => {
      if (idx === prev) return 0;
      if (idx < prev) return Math.max(0, prev - 1);
      return prev;
    });
  }, []);

  const invalidateMedia = useCallback((idx) => {
    setMediaItems((prev) => {
      if (!prev[idx]) return prev;
      const next = [...prev];
      const old = next[idx];
      if (old.preview?.startsWith('blob:')) URL.revokeObjectURL(old.preview);
      next[idx] = {
        preview: '',
        url: '',
        isVideo: false,
        placeholder: true,
        label: 'Draft preview expired.',
        needsReattach: true,
      };
      return next;
    });
  }, []);

  const uploadMediaItems = useCallback(async () => {
    if (!mediaItems.length) return [];
    const reattachNeeded = mediaItems.some(
      (item) => item.needsReattach || item.placeholder,
    );
    if (reattachNeeded) {
      throw new Error('MEDIA_REATTACH_REQUIRED');
    }
    // Drop any stale previews that no longer have a File handle (e.g., after reload)
    const freshMedia = mediaItems.filter((item) => item.file);
    if (freshMedia.length !== mediaItems.length) {
      setMediaItems(freshMedia);
    }
    if (!freshMedia.length) {
      throw new Error('MEDIA_MISSING_FILE');
    }
    const uploaded = [];
    let completed = 0;
    for (const item of freshMedia) {
      const { transformedUrl } = await uploader.upload(item.file, {
        onProgress: (pct) => {
          const slice = (completed + pct / 100) / freshMedia.length;
          setUploadPct(Math.round(slice * 100));
        },
        publicIdPrefix: 'Images',
      });
      uploaded.push({
        url: transformedUrl,
        type: item.isVideo ? 'video' : 'image',
      });
      completed += 1;
    }
    setUploadPct(100);
    return uploaded;
  }, [mediaItems, uploader]);

  const parsedQuantity = useMemo(() => {
    const val = Number(quantityAvailable);
    if (!Number.isFinite(val) || val < 1) return 1;
    return Math.floor(val);
  }, [quantityAvailable]);

  const computeStartDate = useCallback(() => {
    const parsed = parseLocalDateTime(availabilityStart);
    if (customStart && parsed) return parsed;
    return new Date();
  }, [availabilityStart, customStart]);

  const hasKnownBalance = useMemo(
    () => Number.isFinite(playerBalance),
    [playerBalance],
  );
  const hasSufficientBalance = useMemo(() => {
    if (!isTask || escrowRequiredJGK <= 0) return true;
    if (!hasKnownBalance) return true;
    return playerBalance >= escrowRequiredJGK;
  }, [isTask, escrowRequiredJGK, hasKnownBalance, playerBalance]);

  const canPublish = useMemo(() => {
    // Debug assist: surface why publish is blocked (development only)
    const numericPrice = price === '' ? null : Number(price);
    const quantityValue = parsedQuantity;
    const startDate = computeStartDate();
    const endDate = resolveAvailabilityEnd(
      startDate,
      availabilityEnd,
      availabilityEndMode,
    );
    if (!selectedMeta) return false;
    if (isOffering && disabledOfferings) return false;
    if (isTask && disabledTasks) return false;
    if (isTask && !presenceReady) return false;
    if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lon))
      return false;
    if ((isTask || isOffering) && !pickupAddress.trim()) return false;
    if (isDelivery && !deliveryAddress.trim()) return false;
    if (!startDate || !endDate) return false;
    if (
      !requestQuote &&
      (numericPrice === null ||
        Number.isNaN(numericPrice) ||
        numericPrice < 0)
    ) {
      return false;
    }
    if (isOffering && requestQuote) return false;
    if (isOffering && !category.trim()) return false;
    if (isOffering && (!Number.isInteger(quantityValue) || quantityValue < 1)) {
      return false;
    }
    if (isAssistance) {
      if (!destinationAddress.trim()) return false;
      if (!destinationCoordsValid) return false;
      if (trustedPlayers.length === 0) return false;
    }
    return true;
  }, [
    selectedMeta,
    isOffering,
    isTask,
    disabledOfferings,
    disabledTasks,
    presenceReady,
    location,
    pickupAddress,
    deliveryAddress,
    availabilityStart,
    availabilityEnd,
    availabilityEndMode,
    computeStartDate,
    requestQuote,
    price,
    parsedQuantity,
    category,
    isAssistance,
    isDelivery,
    destinationCoordsValid,
    destinationAddress,
    visibility,
    trustedPlayers,
  ]);

  const publishBlockers = useMemo(() => {
    if (canPublish) return [];
    const issues = [];
    const numericPrice = price === '' ? null : Number(price);
    const quantityValue = parsedQuantity;
    const startDate = computeStartDate();
    const endDate = resolveAvailabilityEnd(
      startDate,
      availabilityEnd,
      availabilityEndMode,
    );
    if (!selectedMeta) issues.push('Choose a type');
    if (isTask && disabledTasks) issues.push('Tasks are disabled');
    if (isOffering && disabledOfferings) issues.push('Offerings are disabled');
    if (isTask && !presenceReady) issues.push('Presence not active');
    if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lon))
      issues.push('Pin not set');
    if ((isTask || isOffering) && !pickupAddress.trim())
      issues.push(isOffering ? 'Offering address required' : 'Pickup address required');
    if (isDelivery && !deliveryAddress.trim()) issues.push('Delivery address required');
    if (!startDate || !endDate) issues.push('Availability required');
    if (
      !requestQuote &&
      (numericPrice === null ||
        Number.isNaN(numericPrice) ||
        numericPrice < 0)
    ) {
      issues.push('Price required');
    }
    if (isOffering && requestQuote) issues.push('Offerings cannot request quote');
    if (isOffering && !category.trim()) issues.push('Category required');
    if (isOffering && (!Number.isInteger(quantityValue) || quantityValue < 1)) {
      issues.push('Quantity required');
    }
    if (isAssistance) {
      if (!destinationAddress.trim()) issues.push('Destination address required');
      if (!destinationCoordsValid) issues.push('Destination pin required');
      if (trustedPlayers.length === 0) issues.push('Select at least one trusted player');
    }
    return issues;
  }, [
    canPublish,
    selectedMeta,
    isTask,
    isOffering,
    disabledTasks,
    disabledOfferings,
    presenceReady,
    location,
    pickupAddress,
    deliveryAddress,
    computeStartDate,
    availabilityEnd,
    availabilityEndMode,
    requestQuote,
    price,
    parsedQuantity,
    category,
    isDelivery,
    isAssistance,
    destinationCoordsValid,
    destinationAddress,
    trustedPlayers,
  ]);
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.log('[DEBUG][PUBLISH CHECK]', {
      destination,
      destinationCoordsValid,
      destinationAddressValue,
      blocked: destinationAddressValue && !destinationCoordsValid,
      pickupAddressValue,
      pickupCoordsValid,
      step,
    });
  }

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[DEBUG][STEP VIEW]', {
        step,
        pickupAddressValue,
        destinationAddressValue,
        pickupCoordsValid,
        destinationCoordsValid,
        isPickupGeocoding,
        isDestinationGeocoding,
        location,
        destination,
      });
    }
  }, [
    step,
    pickupAddressValue,
    destinationAddressValue,
    pickupCoordsValid,
    destinationCoordsValid,
    isPickupGeocoding,
    isDestinationGeocoding,
    location,
    destination,
  ]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[DEBUG][DESTINATION STATE]', {
        step,
        destinationAddressValue,
        destination,
        destinationCoordsValid,
      });
    }
  }, [step, destinationAddressValue, destination, destinationCoordsValid]);

  const persistHistory = useCallback((mode, updates) => {
    if (!mode) return;
    setInputHistory((prev) => {
      const next = normalizeHistory(prev);
      const bucket = next[mode] || {};
      Object.entries(updates || {}).forEach(([field, raw]) => {
        if (!field || raw === undefined || raw === null) return;
        if (field === 'media') return;
        const list = Array.isArray(bucket[field]) ? bucket[field] : [];
        const value = String(raw).trim();
        if (!value) return;
        const lower = value.toLowerCase();
        const deduped = [
          value,
          ...list.filter((item) => item.toLowerCase() !== lower),
        ].slice(0, HISTORY_LIMIT);
        bucket[field] = deduped;
      });
      next[mode] = bucket;
      if (typeof window !== 'undefined') {
        try {
          window.localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
        } catch (err) {
          // ignore storage failures
        }
      }
      return next;
    });
  }, []);

  const persistHistoryExternal = useCallback(
    (mode, payload) => {
      if (!mode || !payload) return;
      const updates = {
        title: payload.title || '',
        description: payload.description || '',
        category: payload.category || '',
        price:
          payload.price ?? payload.price === 0 ? String(payload.price) : '',
      };
      if (mode === 'task') {
        updates.pickupAddress = payload.originLocation?.address || '';
        updates.deliveryAddress = payload.meta?.deliveryAddress || '';
      }
      if (mode === 'offering') {
        updates.quantity = payload.quantityAvailable
          ? String(payload.quantityAvailable)
          : '';
      }
      persistHistory(mode, updates);
    },
    [persistHistory],
  );

  const handlePublish = async () => {
    if (!selectedMeta) return;
    setError('');
    if (!canPublish) {
      setError('Please complete the required fields.');
      return;
    }
    setIsSubmitting(true);
    try {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.debug('[LocalOpsCreateSheet][publish:start]', {
          step,
          mode: selectedMeta.mode,
          taskType: selectedMeta.taskType,
          pickupAddressValue,
          destinationAddressValue,
          pickupLocation: location,
          destinationLocation: destination,
          pickupCoordsValid: location && Number.isFinite(location.lat) && Number.isFinite(location.lon),
          destinationCoordsValid:
            destination && Number.isFinite(destination.lat) && Number.isFinite(destination.lon),
        });
      }
      const coordsValid = (loc) =>
        loc &&
        Number.isFinite(loc.lat) &&
        Number.isFinite(loc.lon);
      if (pickupAddressValue && !coordsValid(location)) {
        setIsSubmitting(false);
        setError('Pickup address needs a map pin. Please wait for it to locate or reselect.');
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[LocalOpsCreateSheet][publish:block]', {
            reason: 'pickup coords missing',
            pickupAddressValue,
            location,
          });
        }
        return;
      }
      if (destinationAddressValue && !coordsValid(destination)) {
        setIsSubmitting(false);
        setError('Destination address needs a map pin. Please wait for it to locate or reselect.');
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[LocalOpsCreateSheet][publish:block]', {
            reason: 'destination coords missing',
            destinationAddressValue,
            destination,
          });
        }
        return;
      }
      // Ensure destination pin exists for assistance tasks just-in-time
      if (
        isAssistance &&
        destinationAddressValue &&
        !coordsValid(destination)
      ) {
        try {
          const coords = await getCoordinatesFromAddress(destinationAddressValue);
          setDestination({ lat: coords.lat, lon: coords.lon });
          destinationGeocodeRef.current = destinationAddressValue;
        } catch (err) {
          setIsSubmitting(false);
          setError("Couldn't resolve destination. Please confirm the address.");
          return;
        }
      }
      const numericPrice = price === '' ? null : Number(price);
      const quantityValue = Number(quantityAvailable);
      let media = [];
      try {
        media = await uploadMediaItems();
      } catch (mediaErr) {
        setIsSubmitting(false);
        const message =
          mediaErr?.message === 'MEDIA_REATTACH_REQUIRED'
            ? 'Media will upload when attached before publish.'
            : mediaErr?.message || 'Media will upload when attached before publish.';
        setError(message);
        return;
      }
      const startDate = computeStartDate();
      const endDate = resolveAvailabilityEnd(
        startDate,
        availabilityEnd,
        availabilityEndMode,
      );
      const payloadBase = {
        title: title.trim(),
        description: description.trim(),
        media,
        coverIndex: Number.isInteger(coverIndex) ? coverIndex : 0,
        visibility,
        trustedPlayers:
          visibility === 'trusted' ? trustedPlayers : [],
        availabilityEndMode,
        category: category.trim(),
        locationLabel: locationLabel || '',
      };
      const availabilityWindow = {
        startAt: startDate?.toISOString(),
        endAt: endDate?.toISOString(),
      };
      if (isOffering) {
        const payload = {
          offeringType: selectedMeta.offeringType,
          category: category.trim(),
          description: payloadBase.description || payloadBase.title || 'Offering',
          price: requestQuote ? undefined : numericPrice,
          requestQuote: false,
          quantityAvailable: parsedQuantity,
          location: {
            type: 'Point',
            coordinates: [location.lon, location.lat],
            address: pickupAddress.trim(),
          },
          deliverySupported: !!deliverySupported,
          availabilitySchedule: {
            windows: availabilityWindow.startAt
              ? [
                  {
                    startAt: availabilityWindow.startAt,
                    endAt: availabilityWindow.endAt,
                  },
                ]
              : [],
          },
          meta: {
            ...payloadBase,
            pickupAddress: pickupAddress.trim(),
          },
        };
        if (
          !hasSufficientBalance &&
          Number.isFinite(playerBalance)
        ) {
          const shortfallJGK = Math.max(0, escrowRequiredJGK - playerBalance);
          if (onInsufficientBalance) {
            // eslint-disable-next-line no-console
            console.log('[jgk-intent] insufficient balance', {
              kind: 'create_offering',
              requiredJGK: escrowRequiredJGK,
              playerBalance,
            });
            onInsufficientBalance({
              type: 'create_offering',
              request: payload,
              requiredJGK: escrowRequiredJGK,
              topUp: {
                neededJGK: escrowRequiredJGK,
                shortfallJGK,
                jgkPerUsd: localOpsConfig?.jgkPerUsd,
              },
              uiContext: { sheet: 'create_offering', step },
            });
          } else {
            onRequestTopUp?.();
          }
          setIsSubmitting(false);
          return;
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[LocalOpsCreateSheet][publish:offering:payload]', {
            step,
            pickupAddress: payload.location?.address,
            location: payload.location,
            metaPickupAddress: payload.meta?.pickupAddress,
          });
        }
        await onCreateOffering?.(payload);
      } else {
        const payload = {
          taskType: selectedMeta.taskType,
          title: payloadBase.title,
          description: payloadBase.description,
          originLocation: {
            type: 'Point',
            coordinates: [location.lon, location.lat],
            address: pickupAddress.trim(),
          },
          destinationLocation:
            isAssistance &&
            destination &&
            Number.isFinite(destination.lat) &&
            Number.isFinite(destination.lon)
              ? {
                  type: 'Point',
                  coordinates: [destination.lon, destination.lat],
                  address: destinationAddress.trim(),
                }
              : undefined,
          pickupAddress: pickupAddress.trim(),
          destinationAddress:
            isAssistance && destinationAddress.trim()
              ? destinationAddress.trim()
              : undefined,
          price: requestQuote ? undefined : numericPrice,
          quoteRequested: !!requestQuote,
          availabilityWindow,
          // Presence Phase 2+: prioritize presence-active users by time/routing only.
          // Do NOT gate discovery or hide tasks from non-presence users.
          presenceRequirements: {
            requiresPresence: false,
            requiresTaskTypeOptIn: false,
          },
          liveTrackingEnabled: isAssistance ? true : !!liveTrackingEnabled,
          shareWithTrustedPlayers:
            visibility === 'trusted' ? trustedPlayers : [],
          trustRequirement:
            canShowRequireVerifiedLocal && requireVerifiedLocal
              ? 'verified_local'
              : 'open',
          radiusMeters: radiusMeters || undefined,
          intakeDraftToken: preset?.intakeDraftToken || undefined,
          uiContext: isCollisionSpecialistRidePreset
            ? { rideTemplate: COLLISION_SPECIALIST_RIDE_TEMPLATE }
            : undefined,
          meta: {
            ...payloadBase,
            pickupAddress: pickupAddress.trim(),
            deliveryAddress: isDelivery ? deliveryAddress.trim() : undefined,
          },
          status: 'discoverable',
        };
        if (
          !hasSufficientBalance &&
          Number.isFinite(playerBalance)
        ) {
          const shortfallJGK = Math.max(0, escrowRequiredJGK - playerBalance);
          if (onInsufficientBalance) {
            // eslint-disable-next-line no-console
            console.log('[jgk-intent] insufficient balance', {
              kind: 'create_task',
              requiredJGK: escrowRequiredJGK,
              playerBalance,
            });
            onInsufficientBalance({
              type: 'create_task',
              request: payload,
              requiredJGK: escrowRequiredJGK,
              topUp: {
                neededJGK: escrowRequiredJGK,
                shortfallJGK,
                jgkPerUsd: localOpsConfig?.jgkPerUsd,
              },
              uiContext: {
                sheet: 'create_task',
                step,
                ...(isCollisionSpecialistRidePreset
                  ? { rideTemplate: COLLISION_SPECIALIST_RIDE_TEMPLATE }
                  : {}),
              },
            });
            persistHistoryExternal('task', payload);
          } else {
            onRequestTopUp?.();
          }
          setIsSubmitting(false);
          return;
        }
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.debug('[LocalOpsCreateSheet][publish:task:payload]', {
            step,
            pickupAddress: payload.originLocation?.address,
            destinationAddress: payload.destinationLocation?.address || payload.destinationAddress,
            originLocation: payload.originLocation,
            destinationLocation: payload.destinationLocation,
            metaPickupAddress: payload.meta?.pickupAddress,
            metaDeliveryAddress: payload.meta?.deliveryAddress,
          });
          // eslint-disable-next-line no-console
          console.log(
            '[DEBUG][CLIENT TASK CREATE PAYLOAD]',
            JSON.stringify(maskSensitiveLocalOpsPayload(payload), null, 2),
          );
        }
        await onCreateTask?.(payload);
      }
      if (historyMode) {
        const historyPayload = {
          title: title.trim(),
          description: description.trim(),
          category: category.trim(),
        };
        if (
          !requestQuote &&
          numericPrice !== null &&
          Number.isFinite(numericPrice)
        ) {
          historyPayload.price = String(numericPrice);
        }
        if (historyMode === 'offering') {
          historyPayload.quantity = String(parsedQuantity);
        }
        if (historyMode === 'task') {
          historyPayload.pickupAddress = pickupAddress.trim();
          if (isDelivery) {
            historyPayload.deliveryAddress = deliveryAddress.trim();
          }
          if (isAssistance && destinationAddress.trim()) {
            historyPayload.deliveryAddress = destinationAddress.trim();
          }
        }
        persistHistory(historyMode, historyPayload);
      }
      if (draftKey) {
        try {
          window.localStorage.removeItem(draftKey);
        } catch (err) {
          // ignore
        }
      }
      setIsSubmitting(false);
      onClose?.();
    } catch (err) {
      setError(err.message || 'Publish failed.');
      setIsSubmitting(false);
    }
  };

  const typeDisabled = (option) => {
    if (option.mode === 'offering') {
      if (disabledOfferings) return true;
      if (
        LOCALOPS_PRESENCE_GATING_ENABLED &&
        presenceSelf?.taskEligibility &&
        presenceSelf.taskEligibility.offering === false
      )
        return true;
      return false;
    }
    if (disabledTasks) return true;
    if (
      LOCALOPS_PRESENCE_GATING_ENABLED &&
      presenceSelf?.taskEligibility &&
      option.taskType
    ) {
      const eligible = presenceSelf.taskEligibility[option.taskType];
      if (eligible === false) return true;
    }
    return false;
  };
  const filteredFriends = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();
    if (!search) return friendOptions;
    return friendOptions.filter((friend) => {
      const haystack = [
        friend.displayName,
        friend.playerId,
        friend.label,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [friendOptions, searchTerm]);

  return (
    <LocalOpsSheet
      open={open}
      onClose={onClose}
      title={headerTitle}
      className={sheetClassName}
      headerRight={
        <button
          type="button"
          className="localops-info-btn"
          onClick={() => setShowInfo((prev) => !prev)}
          aria-label="What is LocalOps"
        >
          <InfoOutlinedIcon fontSize="small" />
        </button>
      }
    >
      <div className="localops-section">
        <div className="localops-helper">Step {step + 1} of 3</div>
      </div>
      {showInfoCard ? (
        <div className="localops-info-card">{INFO_COPY}</div>
      ) : null}
      {step === 0 ? (
        <div className="localops-section">
          <h4>Choose a type</h4>
          <div className="localops-helper">
            Offerings are what you provide. Tasks are what you need done.
          </div>
          <div className="localops-type-group">
            <div className="localops-type-group__title">Tasks</div>
            <div className="localops-type-grid">
              {TYPE_OPTIONS.filter((opt) => opt.group === 'tasks').map((opt) => {
                const Icon = opt.Icon;
                const isDisabled = typeDisabled(opt);
                const isActive = selectedType === opt.id;
                return (
                  <div
                    key={opt.id}
                    role="button"
                    tabIndex={isDisabled ? -1 : 0}
                    aria-disabled={isDisabled}
                    className={`localops-type-card ${
                      isActive ? 'is-active' : ''
                    } ${isDisabled ? 'is-disabled' : ''}`}
                    onClick={() => {
                      if (isDisabled) return;
                      handleSelectType(opt.id);
                    }}
                    onKeyDown={(event) => {
                      if (isDisabled) return;
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSelectType(opt.id);
                      }
                    }}
                  >
                    <span className="localops-type-card__icon">
                      <Icon fontSize="small" />
                    </span>
                    <span className="localops-type-card__body">
                      <span className="localops-type-card__title">
                        {opt.label}
                      </span>
                      <span className="localops-type-card__desc">
                        {opt.description}
                      </span>
                    </span>
                    <button
                      type="button"
                      className="localops-type-card__info"
                      data-tooltip={opt.tooltip}
                      onClick={(event) => {
                        event.stopPropagation();
                        if (!mobileView) return;
                        setActiveTypeInfo((prev) =>
                          prev === opt.id ? null : opt.id,
                        );
                      }}
                      aria-label={`${opt.label} info`}
                    >
                      i
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="localops-type-group">
            <div className="localops-type-group__title">Offerings</div>
            <div className="localops-type-grid">
              {TYPE_OPTIONS.filter((opt) => opt.group === 'offerings').map(
                (opt) => {
                  const Icon = opt.Icon;
                  const isDisabled = typeDisabled(opt);
                  const isActive = selectedType === opt.id;
                  return (
                    <div
                      key={opt.id}
                      role="button"
                      tabIndex={isDisabled ? -1 : 0}
                      aria-disabled={isDisabled}
                      className={`localops-type-card ${
                        isActive ? 'is-active' : ''
                      } ${isDisabled ? 'is-disabled' : ''}`}
                      onClick={() => {
                        if (isDisabled) return;
                        handleSelectType(opt.id);
                      }}
                      onKeyDown={(event) => {
                        if (isDisabled) return;
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleSelectType(opt.id);
                        }
                      }}
                    >
                      <span className="localops-type-card__icon">
                        <Icon fontSize="small" />
                      </span>
                      <span className="localops-type-card__body">
                        <span className="localops-type-card__title">
                          {opt.label}
                        </span>
                        <span className="localops-type-card__desc">
                          {opt.description}
                        </span>
                      </span>
                      <button
                        type="button"
                        className="localops-type-card__info"
                        data-tooltip={opt.tooltip}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (!mobileView) return;
                          setActiveTypeInfo((prev) =>
                            prev === opt.id ? null : opt.id,
                          );
                        }}
                        aria-label={`${opt.label} info`}
                      >
                        i
                      </button>
                    </div>
                  );
                },
              )}
            </div>
          </div>
          {mobileView && activeTypeInfo ? (
            <div className="localops-info-card">
              {TYPE_OPTIONS.find((opt) => opt.id === activeTypeInfo)?.tooltip}
            </div>
          ) : null}
        </div>
      ) : null}

      {step === 1 && selectedMeta ? (
        <>
          <div className="localops-section">
            <h4>Details</h4>
            <div className="localops-row">
              <div className="localops-field">
                <label>Title</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Short title"
                  list="localops-title-history"
                  autoComplete="on"
                  name={
                    isOffering
                      ? 'localops-offering-title'
                      : 'localops-task-title'
                  }
                  onFocus={() => {
                    if (!title.trim()) setFocusedField('title');
                  }}
                  onBlur={() => setFocusedField(null)}
                />
                <datalist id="localops-title-history">
                  {titleHistory.map((item, index) => (
                    <option
                      value={item}
                      key={`localops-title-${index}`}
                    />
                  ))}
                </datalist>
                {focusedField === 'title' && !title.trim() && recentTitleChips.length ? (
                  <div className="localops-input-history">
                    <div className="localops-input-history__label">Recent</div>
                    <div className="localops-input-history__list">
                      {recentTitleChips.map((item, index) => (
                        <button
                          key={`localops-title-chip-${index}`}
                          type="button"
                          className="localops-input-history__chip"
                          onMouseDown={(event) => {
                            event.preventDefault();
                            setTitle(item);
                          }}
                        >
                          {item}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
              <div className="localops-field">
                <label>{isOffering ? 'Category (required)' : 'Category (optional)'}</label>
                <input
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="Select or type a category"
                  list="localops-category-list"
                  autoComplete="on"
                  name={
                    isOffering
                      ? 'localops-offering-category'
                      : 'localops-task-category'
                  }
                />
                <datalist id="localops-category-list">
                  {categoryOptions.map((item) => (
                    <option value={item} key={item} />
                  ))}
                </datalist>
              </div>
            </div>
              <div className="localops-field">
                <label>Description</label>
                <textarea
                  value={description}
                  onChange={(e) => {
                    setDescription(e.target.value);
                    setDescriptionAuto(false);
                  }}
                  placeholder="Add a short description"
                  autoComplete="on"
                  name={
                    isOffering
                      ? 'localops-offering-description'
                    : 'localops-task-description'
                }
                onFocus={() => setFocusedField('description')}
                onBlur={() => setFocusedField(null)}
              />
            </div>
            {isOffering ? (
              <div className="localops-helper">
                People nearby can't buy what they can't see.
              </div>
            ) : null}
            {isTask ? (
              <div className="localops-helper">
                Clear details help the right person step in.
              </div>
            ) : null}
            {descriptionAuto === false && title.trim() && description.trim() !== title.trim() ? (
              <div className="localops-input-history">
                <button
                  type="button"
                  className="localops-input-history__chip"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    setDescription(title);
                    setDescriptionAuto(true);
                  }}
                >
                  Use title as description
                </button>
              </div>
            ) : null}
            {showDescriptionHistory ? (
              <div className="localops-input-history">
                <div className="localops-input-history__label">Recent</div>
                <div className="localops-input-history__list">
                  {descriptionSuggestions.map((item, index) => (
                    <button
                      key={`localops-desc-${index}`}
                      type="button"
                      className="localops-input-history__chip"
                      onMouseDown={(event) => {
                        event.preventDefault();
                        setDescription(item);
                      }}
                    >
                      {item}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
          <div className="localops-section">
            <h4>Location</h4>
            <div className="localops-pin-card">
              <div className="localops-pin-card__meta">
                <span className="localops-pin-card__title">Pinned to map</span>
                <span className="localops-pin-card__desc">
                  {isResolvingLocation
                    ? 'Resolving location...'
                    : locationLabel || 'Set a point on the map'}
                </span>
              </div>
              <button
                type="button"
                className="localops-btn localops-btn--ghost"
                onClick={() => {
                  if (!defaultCenter) return;
                  setLocation({ ...defaultCenter });
                  setPinSource('manual');
                  setPinNotice('');
                  setPinError('');
                }}
              >
                <MyLocationOutlinedIcon fontSize="small" />
                Use my location
              </button>
            </div>
            {pickupCoordsValid ? (
              <div className="localops-earn-row" style={{ marginTop: 6 }}>
                <CheckCircleOutlineIcon className="localops-earn-icon" fontSize="small" />
                <span>Location confirmed.</span>
              </div>
            ) : pickupAddressValue ? (
              <div className="localops-helper" style={{ color: '#f97316', marginTop: 6 }}>
                Waiting to place pin from address...
              </div>
            ) : null}
            {isTask || isOffering ? (
              <div className="localops-field">
                <label>
                  {isOffering
                    ? 'Offering address (private, required)'
                    : 'Pickup address (private, required)'}
                </label>
                <input
                  value={pickupAddress}
                  onChange={(e) => setPickupAddress(e.target.value)}
                  placeholder="Street, city, state"
                  list="localops-pickup-history"
                  autoComplete="street-address"
                  name="localops-pickup-address"
                />
                <datalist id="localops-pickup-history">
                  {pickupHistory.map((item, index) => (
                    <option
                      value={item}
                      key={`localops-pickup-${index}`}
                    />
                  ))}
                </datalist>
                <div className="localops-helper">
                  {isOffering
                    ? 'Where buyers pick up your offering. Shared only after a buyer reserves.'
                    : isCollisionSpecialistRidePreset
                    ? 'Pickup point for the ride. Shared only after a helper accepts.'
                    : isDelivery
                    ? 'This is where the item will be picked up. Shared only after a helper accepts.'
                    : 'This is the place for the task. Shared only after a helper accepts.'}
                </div>
                <div className="localops-helper">
                  {isPickupGeocoding
                    ? 'Locating pin from address...'
                    : 'Pin automatically placed near location.'}
                </div>
                {pickupCoordsValid ? (
                  <div className="localops-earn-row">
                    <CheckCircleOutlineIcon
                      className="localops-earn-icon"
                      fontSize="small"
                    />
                    <span>{pinNotice || 'Pin placed near location.'}</span>
                  </div>
                ) : pinNotice ? (
                  <div className="localops-earn-row">
                    <CheckCircleOutlineIcon
                      className="localops-earn-icon"
                      fontSize="small"
                    />
                    <span>{pinNotice}</span>
                  </div>
                ) : null}
                {!pickupCoordsValid && pinError ? (
                  <div className="localops-error">{pinError}</div>
                ) : null}
              </div>
            ) : null}
            {isTask && isDelivery ? (
              <div className="localops-field">
                <label>
                  {isCollisionSpecialistRidePreset
                    ? 'Destination address (private, required)'
                    : 'Delivery address (private)'}
                </label>
                <input
                  value={deliveryAddress}
                  onChange={(e) => setDeliveryAddress(e.target.value)}
                  placeholder="Street, city, state"
                  list="localops-delivery-history"
                  autoComplete="street-address"
                  name="localops-delivery-address"
                />
                <datalist id="localops-delivery-history">
                  {deliveryHistory.map((item, index) => (
                    <option
                      value={item}
                      key={`localops-delivery-${index}`}
                    />
                  ))}
                </datalist>
                <div className="localops-helper">
                  {isCollisionSpecialistRidePreset
                    ? 'Where do you need to go? This is only shared after a helper accepts.'
                    : 'Where should the item be delivered? This is only shared after a helper accepts.'}
                </div>
              </div>
            ) : null}
            {isAssistance ? (
              <div className="localops-field">
                <label>Destination address (private)</label>
                <input
                  value={destinationAddress}
                  onChange={(e) => setDestinationAddress(e.target.value)}
                  placeholder="Street, city, state"
                  list="localops-destination-history"
                  autoComplete="street-address"
                  name="localops-destination-address"
                />
                <datalist id="localops-destination-history">
                  {destinationHistory.map((item, index) => (
                    <option
                      value={item}
                      key={`localops-destination-${index}`}
                    />
                  ))}
                </datalist>
                <div className="localops-helper">
                  Where should you be dropped off? Shared only after a helper accepts.
                </div>
                <div className="localops-helper">
                  {isDestinationGeocoding
                    ? 'Locating pin from destination address...'
                    : 'Pin automatically placed near destination.'}
                </div>
                {destinationCoordsValid ? (
                  <div className="localops-earn-row">
                    <CheckCircleOutlineIcon className="localops-earn-icon" />
                    <div className="localops-earn-text">
                      {destinationPinNotice || 'Pin placed near destination.'}
                    </div>
                  </div>
                ) : destinationPinNotice ? (
                  <div className="localops-earn-row">
                    <CheckCircleOutlineIcon className="localops-earn-icon" />
                    <div className="localops-earn-text">{destinationPinNotice}</div>
                  </div>
                ) : null}
                {!destinationCoordsValid && destinationPinError ? (
                  <div className="localops-error">{destinationPinError}</div>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="localops-section">
            <h4>Price</h4>
            <div className="localops-row">
              <div className="localops-field">
                <label>{isOffering ? 'Unit price ($)' : 'Amount ($)'}</label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  disabled={requestQuote}
                  list="localops-price-history"
                  autoComplete="on"
                  name={
                    isOffering
                      ? 'localops-offering-price'
                      : 'localops-task-price'
                  }
                  onWheel={(e) => {
                    // Prevent mouse wheel from accidentally changing amount
                    e.target.blur();
                    e.preventDefault();
                  }}
                />
                <datalist id="localops-price-history">
                  {priceHistory.map((item, index) => (
                    <option
                      value={item}
                      key={`localops-price-${index}`}
                    />
                  ))}
                </datalist>
                {isCollisionSpecialistRidePreset ? (
                  <div className="localops-helper">
                    {rideSuggestedPrice && rideSuggestedDistanceLabel
                      ? `Suggested price based on estimated distance. You can adjust before posting. Estimated distance: ${rideSuggestedDistanceLabel}.`
                      : isRidePriceGeocoding
                      ? 'Estimating suggested price from the destination...'
                      : 'Suggested price based on estimated distance. You can adjust before posting.'}
                  </div>
                ) : null}
              </div>
            </div>
            {isOffering ? (
              <>
                <div className="localops-row">
                  <div className="localops-field">
                    <label>Quantity available</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={quantityAvailable}
                      onChange={(e) => setQuantityAvailable(e.target.value)}
                    />
                  </div>
                </div>
                <div className="localops-helper">
                  Set how many units you can fulfill right now.
                </div>
              </>
            ) : (
              <div className="localops-row">
                <button
                  type="button"
                  className={`localops-pill-toggle ${
                    requestQuote ? 'is-active' : ''
                  }`}
                  onClick={() => {
                    setRequestQuote((prev) => !prev);
                    if (!requestQuote) setPrice('');
                  }}
                >
                  Request quote
                </button>
                <button
                  type="button"
                  className={`localops-pill-toggle ${isFree ? 'is-active' : ''}`}
                  onClick={() => {
                    setRequestQuote(false);
                    setPrice(isFree ? '' : '0');
                  }}
                >
                  Free
                </button>
              </div>
            )}
            {isTask ? (
              <div className="localops-helper">
                {hasPaidTaskAmount
                  ? "Your JGK is held until the task is completed or canceled. Helper payout is released after you verify the work. Verified completion strengthens the helper's Action Credit record."
                  : 'Choose a paid amount, request a quote, or mark this task free.'}
              </div>
            ) : null}
          </div>
          {isOffering ? (
            <div className="localops-section">
              <h4>Delivery</h4>
              <label className="localops-pill-toggle">
                <input
                  type="checkbox"
                  checked={deliverySupported}
                  onChange={(e) => setDeliverySupported(e.target.checked)}
                />
                Delivery supported
              </label>
              <div className="localops-helper">
                Enable if you are willing to have this delivered by a local
                helper.
              </div>
            </div>
          ) : null}
          <LocalOpsMediaPicker
            mediaItems={mediaItems}
            coverIndex={coverIndex}
            onCoverChange={setCoverIndex}
            onRemove={removeMedia}
            onInvalidate={invalidateMedia}
            onAddFiles={addFiles}
            error={error}
            uploadPct={uploadPct}
            warning={mediaWarning}
          />
          <div className="localops-section">
            <h4>Public preview (what others will see)</h4>
            <div className="localops-preview-card">
              <div className="localops-preview-title">{previewData.title}</div>
              {previewData.description ? (
                <div className="localops-preview-desc">
                  {previewData.description}
                </div>
              ) : null}
              <div className="localops-preview-meta">
                <span className="localops-preview-badge">
                  {previewData.category}
                </span>
                <span className="localops-preview-price">
                  {previewData.priceLabel}
                </span>
                {previewData.quantityLabel ? (
                  <span className="localops-preview-badge">
                    {previewData.quantityLabel}
                  </span>
                ) : null}
                <span>{previewData.location}</span>
                {previewData.endMode ? (
                  <span className="localops-preview-badge">
                    {previewData.endMode}
                  </span>
                ) : null}
              </div>
              <div className="localops-preview-media">
                {previewData.media.length
                  ? previewData.media.map((item, idx) => (
                      <div className="localops-preview-thumb" key={idx}>
                        {item.placeholder ? (
                          <div className="localops-preview-thumb__placeholder">
                            {item.label || 'Draft preview expired.'}
                          </div>
                        ) : item.isVideo ? (
                          <video
                            src={item.preview}
                            muted
                            playsInline
                            preload="metadata"
                            onError={() => invalidateMedia(idx)}
                          />
                        ) : (
                          <img
                            src={item.preview}
                            alt=""
                            onError={() => invalidateMedia(idx)}
                          />
                        )}
                        {item.preview?.startsWith('blob:') ? (
                          <div className="localops-preview-thumb__badge">
                            Draft preview
                          </div>
                        ) : null}
                      </div>
                    ))
                  : Array.from({ length: 3 }).map((_, idx) => (
                      <div
                        key={`placeholder-${idx}`}
                        className="localops-preview-thumb localops-preview-placeholder"
                      >
                        Add media
                      </div>
                    ))}
              </div>
            </div>
          </div>
          <button
            type="button"
            className="localops-adv-toggle"
            onClick={() => setShowAdvanced((prev) => !prev)}
          >
            {showAdvanced ? 'Hide advanced options' : 'Advanced options'}
          </button>
          {showAdvanced ? (
            <>
              <div className="localops-section">
                <h4>Availability</h4>
                <div className="localops-row">
                  <div className="localops-field">
                    <label>Start</label>
                    <input
                      type="datetime-local"
                      value={availabilityStart}
                      onChange={(e) => {
                        setAvailabilityStart(e.target.value);
                        setCustomStart(!!e.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="localops-row">
                  <div className="localops-field">
                    <label>Ends</label>
                    <select
                      value={availabilityEndMode}
                      onChange={(e) => setAvailabilityEndMode(e.target.value)}
                    >
                      {END_MODE_OPTIONS.map((option) => (
                        <option value={option.id} key={option.id}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {availabilityEndMode === 'specific' ? (
                    <div className="localops-field">
                      <label>End</label>
                      <input
                        type="datetime-local"
                        value={availabilityEnd}
                        onChange={(e) => setAvailabilityEnd(e.target.value)}
                      />
                    </div>
                  ) : null}
                </div>
                {availabilityEndMode !== 'specific' ? (
                  <div className="localops-helper">
                    {labelForEndMode(availabilityEndMode)} keeps this live while
                    you plan. Update any time.
                  </div>
                ) : null}
              </div>
              <div className="localops-section">
                <h4>Advanced coordinates</h4>
                <div className="localops-row localops-adv-fields">
                  <div className="localops-field">
                    <label>Latitude</label>
                    <input
                      type="number"
                      value={location?.lat ?? ''}
                      onChange={(e) =>
                        setLocation((prev) => {
                          setPinSource('manual');
                          setPinNotice('');
                          setPinError('');
                          return {
                            ...(prev || {}),
                            lat: Number(e.target.value),
                          };
                        })
                      }
                    />
                  </div>
                  <div className="localops-field">
                    <label>Longitude</label>
                    <input
                      type="number"
                      value={location?.lon ?? ''}
                      onChange={(e) =>
                        setLocation((prev) => {
                          setPinSource('manual');
                          setPinNotice('');
                          setPinError('');
                          return {
                            ...(prev || {}),
                            lon: Number(e.target.value),
                          };
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {step === 2 && selectedMeta ? (
        <div className="localops-section">
          <h4>Safety & visibility</h4>
          <div className="localops-helper">
            Set task discovery and accept eligibility.
          </div>
          <div className="localops-choice-label">
            Who can see this task?
          </div>
          <div className="localops-visibility-grid localops-choice-grid">
            <button
              type="button"
              className={`localops-visibility-card ${
                visibility === 'nearby' ? 'is-active' : ''
              }`}
              onClick={() => setVisibility('nearby')}
            >
              <div className="localops-visibility-title">Nearby</div>
              <div className="localops-visibility-desc">
                Visible to LocalOps players nearby.
              </div>
            </button>
            <button
              type="button"
              className={`localops-visibility-card ${
                visibility === 'trusted' ? 'is-active' : ''
              }`}
              onClick={() => setVisibility('trusted')}
            >
              <div className="localops-visibility-title">Trusted</div>
              <div className="localops-visibility-desc">
                Only people you choose can see this.
              </div>
            </button>
          </div>
          {visibility === 'trusted' ? (
            <div className="localops-section">
              <div className="localops-trusted-header">
                <span>Trusted players</span>
                <span className="localops-helper">
                  Select who can see this.
                </span>
              </div>
              {trustedPlayers.length ? (
                <div className="localops-chip-row">
                  {trustedPlayers.map((id) => {
                    const friend = friendOptions.find((f) => f.id === id);
                    const label =
                      friend?.playerId || friend?.label || 'JG Player';
                    return (
                      <button
                        key={id}
                        type="button"
                        className="localops-chip"
                        onClick={() => toggleTrusted(id)}
                      >
                        {label}
                        <span className="localops-chip__remove">x</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <label className="localops-search">
                <SearchOutlinedIcon fontSize="small" />
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search playerId or name"
                />
              </label>
              <div className="localops-trusted-list">
                {filteredFriends.map((friend) => {
                  const isSelected = trustedPlayers.includes(friend.id);
                  const label =
                    friend.displayName ||
                    friend.playerId ||
                    friend.label ||
                    'JG Player';
                  const showHandle =
                    friend.playerId &&
                    friend.displayName &&
                    friend.displayName !== friend.playerId;
                  return (
                    <button
                      key={friend.id}
                      type="button"
                      className={`localops-trusted-row ${
                        isSelected ? 'is-selected' : ''
                      }`}
                      onClick={() => toggleTrusted(friend.id)}
                    >
                      <span className="localops-avatar">
                        {friend.photo ? (
                          <img src={friend.photo} alt="" />
                        ) : (
                          <span className="localops-avatar__fallback">
                            {String(friend.label || friend.id || '?')
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        )}
                        {friend.presence ? (
                          <span
                            className={`localops-presence-dot ${
                              friend.presence === 'busy'
                                ? 'is-busy'
                                : friend.presence === 'offline'
                                ? 'is-offline'
                                : ''
                            }`}
                          />
                        ) : null}
                      </span>
                      <span className="localops-trusted-meta">
                        <span className="localops-trusted-label">{label}</span>
                        {showHandle ? (
                          <span className="localops-trusted-sub">
                            @{friend.playerId}
                          </span>
                        ) : null}
                      </span>
                      <span
                        className={`localops-toggle-switch ${
                          isSelected ? 'is-on' : ''
                        }`}
                      >
                        <span className="localops-toggle-switch__thumb" />
                      </span>
                    </button>
                  );
                })}
                {!filteredFriends.length ? (
                  <div className="localops-helper">
                    No matching players found.
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
          {canShowRequireVerifiedLocal ? (
            <div className="localops-section">
              <div className="localops-choice-label">
                Who can accept this task?
              </div>
              <div
                className="localops-visibility-grid localops-choice-grid"
                role="radiogroup"
                aria-label="Acceptance requirement"
              >
                <label
                  className={`localops-visibility-card ${
                    !requireVerifiedLocal ? 'is-active' : ''
                  }`}
                >
                  <input
                    className="localops-card-radio-input"
                    type="radio"
                    name="taskAcceptanceRequirement"
                    checked={!requireVerifiedLocal}
                    onChange={() => setRequireVerifiedLocal(false)}
                  />
                  <div className="localops-visibility-title">
                    Anyone Nearby
                  </div>
                  <div className="localops-visibility-desc">
                    Recommended for faster help from available LocalOps helpers.
                  </div>
                </label>
                <label
                  className={`localops-visibility-card ${
                    requireVerifiedLocal ? 'is-active' : ''
                  }`}
                >
                  <input
                    className="localops-card-radio-input"
                    type="radio"
                    name="taskAcceptanceRequirement"
                    checked={requireVerifiedLocal}
                    onChange={() => setRequireVerifiedLocal(true)}
                  />
                  <div className="localops-visibility-title">
                    Verified Local only
                  </div>
                  <div className="localops-visibility-desc">
                    Only helpers with Action Credit 1+ can accept. This may
                    reduce the helper pool.
                  </div>
                </label>
              </div>
              <div className="localops-helper">
                Action Credit is verified proof from completed local work.
              </div>
            </div>
          ) : null}
          {hasPaidTaskAmount ? (
            <div className="localops-section">
              {isCollisionSpecialistRidePreset ? (
                <div
                  className="localops-ride-review"
                  data-testid="collision-specialist-ride-review"
                >
                  <div className="localops-ride-review__title">Ride request</div>
                  <div className="localops-ride-review__row">
                    <span>Pickup</span>
                    <strong>
                      Collision Specialist
                      <br />
                      134 West Main Street, Camden, TN 38320
                    </strong>
                  </div>
                  <div className="localops-ride-review__row">
                    <span>Destination</span>
                    <strong>{deliveryAddressValue || 'Not set yet'}</strong>
                  </div>
                  <div className="localops-ride-review__row">
                    <span>Suggested amount</span>
                    <strong>{rideReviewAmountLabel}</strong>
                  </div>
                </div>
              ) : null}
              <h4>{isCollisionSpecialistRidePreset ? 'Ride funding' : 'Task funding'}</h4>
              <div className="localops-helper">
                {isCollisionSpecialistRidePreset
                  ? `Reserve ${escrowLabel} (${escrowUsdLabel}) to request ride help.`
                  : `Reserve ${escrowLabel} (~${escrowUsdLabel}) to publish this task.`}
              </div>
              <div className="localops-helper">
                Your JGK is held until the task is completed or canceled.
              </div>
              <div className="localops-helper">
                {isCollisionSpecialistRidePreset
                  ? 'Helper payout is released after you verify the ride is complete.'
                  : 'Helper payout is released after you verify the work.'}
              </div>
              <div className="localops-helper">
                Verified completion strengthens the helper's Action Credit
                record.
              </div>
              <div className="localops-helper">
                Platform fee: {feeLabel} (only after completion).
              </div>
              <div className="localops-helper">
                We take a small platform fee to keep LocalOps running.
              </div>
              <LocalOpsPayWithBalance
                required={escrowRequiredJGK}
                balance={playerBalance}
                onTopUp={onRequestTopUp}
                label="Amount reserved"
                showTopUpButton={false}
                requiredLabelOverride={
                  isCollisionSpecialistRidePreset ? escrowLabel : null
                }
                usdApproximate={!isCollisionSpecialistRidePreset}
              />
            </div>
          ) : null}
          {isTask ? (
            <div className="localops-section">
              <label className="localops-pill-toggle">
                <input
                  type="checkbox"
                  checked={isAssistance ? true : liveTrackingEnabled}
                  disabled={isAssistance}
                  onChange={(e) => setLiveTrackingEnabled(e.target.checked)}
                />
                Live tracking {isAssistance ? '(required)' : ''}
              </label>
              <div className="localops-helper">
                Live tracking helps everyone feel safe.
              </div>
            </div>
          ) : null}
          {!presenceReady && isTask ? (
            <div className="localops-error">
              Turn on Live Location to publish. This helps nearby helpers find
              you and keeps it safe.
            </div>
          ) : null}
        </div>
      ) : null}

      {error ? <div className="localops-error">{error}</div> : null}
      {!canPublish && publishBlockers.length ? (
        <div className="localops-helper localops-helper--warn">
          {publishBlockers.map((item) => (
            <div key={item}>• {item}</div>
          ))}
        </div>
      ) : null}
      {pendingIntent && pendingIntent.state === 'pending' && hasSufficientBalance ? (
        <div className="localops-section">
          <div className="localops-helper">
            We couldn't finish publishing. Tap retry to continue.
          </div>
          <button
            type="button"
            className="localops-btn"
            onClick={onRetryPendingIntent}
          >
            Retry publish
          </button>
        </div>
      ) : null}

      <div className="localops-actions">
        {step > 0 ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={() => setStep((prev) => Math.max(0, prev - 1))}
          >
            Back
          </button>
        ) : null}
        {step > 0 ? (
          <button
            type="button"
            className="localops-btn localops-btn--ghost"
            onClick={() =>
              resetForm({
                nextType: selectedType,
                nextStep: selectedType ? 1 : 0,
                keepLocation: false,
              })
            }
          >
            Reset
          </button>
        ) : null}
        {step < 2 && selectedMeta ? (
          <button
            type="button"
            className="localops-btn"
            onClick={() => setStep((prev) => Math.min(2, prev + 1))}
          >
            Next
          </button>
        ) : null}
        {step === 2 ? (
          !hasSufficientBalance ? (
            <button
              type="button"
              className="localops-btn"
              disabled={!canPublish || isSubmitting}
              onClick={handlePublish}
            >
              Add JGK Balance
            </button>
          ) : (
          <button
            type="button"
            className="localops-btn"
            disabled={!canPublish || isSubmitting}
            onClick={handlePublish}
          >
            {publishLabel}
          </button>
          )
        ) : null}
      </div>
    </LocalOpsSheet>
  );
}
